import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionAPI } from '../services/api';
import {
    Box,
    Paper,
    Typography,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Alert
} from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EventIcon from '@mui/icons-material/Event';

/**
 * Component to display the user's current subscription status
 * Shows subscription plan, status, expiration date, and options to manage subscription
 */
const SubscriptionStatus = () => {
    const { user, refreshUserData } = useAuth();
    const navigate = useNavigate();
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cancelling, setCancelling] = useState(false);

    useEffect(() => {
        // Only try to fetch subscription if user is logged in
        if (user) {
            // Use a ref to track if this is the first mount to prevent repeated fetches
            const shouldFetch = !subscription;
            if (shouldFetch) {
                fetchSubscription();
            }
        } else {
            setLoading(false);
        }
    }, [user]); // Only depend on user, not on refreshUserData

    const fetchSubscription = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Don't automatically refresh user data here - that's causing the refresh loops
            // Only fetch the subscription details
            const data = await subscriptionAPI.getCurrentSubscription();
            setSubscription(data);
        } catch (err) {
            // If no subscription is found, use the information from the user object
            if (err.response && err.response.status === 404) {
                if (user?.subscription) {
                    // If we have subscription data in the user object, use that
                    setSubscription({
                        status: user.subscription.status || 'free',
                        planType: user.subscription.planType || 'free',
                        endDate: user.subscription.currentPeriodEnd || new Date(),
                        autoRenew: user.subscription.autoRenew !== false // Default to true if not explicitly set to false
                    });
                } else {
                    setSubscription(null);
                }
            } else {
                console.error('Error fetching subscription:', err);
                setError('Failed to load subscription details.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleManageSubscription = () => {
        navigate('/subscription');
    };

    const handleCancelSubscription = async () => {
        if (window.confirm('Are you sure you want to cancel your subscription? Your benefits will remain active until the end of the current billing period.')) {
            try {
                setLoading(true);
                await subscriptionAPI.cancelSubscription();
                await fetchSubscription(); // Refresh subscription status
            } catch (err) {
                console.error('Error cancelling subscription:', err);
                setError('Failed to cancel subscription. Please try again.');
            } finally {
                setLoading(false);
            }
        }
    };

    // Format date to display in a friendly format
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        }).format(date);
    };

    // Get status color based on subscription status
    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'trialing':
            case 'trial':
                return 'info';
            case 'canceled':
            case 'cancelled':
                return 'warning';
            case 'unpaid':
            case 'expired':
                return 'error';
            default:
                return 'default';
        }
    };

    // Get plan display name
    const getPlanDisplayName = (planType) => {
        switch (planType) {
            case 'basic':
                return 'ReaderFlex Basic';
            case 'pro':
                return 'ReaderFlex Pro';
            case 'team':
                return 'ReaderFlex Team';
            default:
                return 'ReaderFlex Membership';
        }
    };

    // Loading state
    if (loading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={30} />
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 3 }}>
                {error}
            </Alert>
        );
    }

    // If no active subscription data found
    if (!subscription || (subscription.status !== 'active' && subscription.status !== 'trialing')) {
        return (
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Subscription Status
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    You currently don't have an active subscription.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/subscription')}
                >
                    View Subscription Plans
                </Button>
            </Paper>
        );
    }

    // Active subscription
    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
                Subscription Status
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="body1" sx={{ mr: 1 }}>
                    Plan:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                    ReaderFlex {subscription.planType.charAt(0).toUpperCase() + subscription.planType.slice(1)}
                </Typography>
                <Chip 
                    size="small" 
                    label="Active" 
                    color="success" 
                    sx={{ ml: 1 }}
                />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                    Renewal Date: {new Date(subscription.endDate).toLocaleDateString()}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AutorenewIcon fontSize="small" sx={{ mr: 1, color: subscription.autoRenew !== false ? 'success.main' : 'text.secondary' }} />
                <Typography variant="body2">
                    Auto-Renewal: <strong>{subscription.autoRenew !== false ? 'Enabled' : 'Disabled'}</strong>
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Button
                    variant="outlined"
                    onClick={() => navigate('/subscription')}
                    startIcon={<WorkspacePremiumIcon />}
                >
                    MANAGE SUBSCRIPTION
                </Button>
                
                {subscription.status === 'active' && (
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={handleCancelSubscription}
                        disabled={cancelling}
                    >
                        {cancelling ? <CircularProgress size={24} /> : 'CANCEL SUBSCRIPTION'}
                    </Button>
                )}
            </Box>
        </Paper>
    );
};

export default SubscriptionStatus;
